import React from 'react';
import { useTranslation } from 'react-i18next';
import { Control } from 'react-hook-form';
import Dropdown from '@common/components/dropdown';
import Icon from '@common/components/icon';
import Collapsible from '@common/components/collapsible';
import { ConfirmButton } from '@common/components/confirm-button';
import { Group, Row, FormLabelHookForm } from '@common/components/form';
import { TextInputHookForm } from '@common/components/form/inputs/text';
import { OptionsInputHookForm } from '@modules/learning/forms/fields/options';
import { DndProvider } from 'react-dnd';
import dndManager from '@common/utils/dnd-manager';
import { OverviewSection } from '@common/components/overview';

interface QuizModuleProps {
  quizModule: {
    title: string;
    type: 'quiz';
    duration: string;
    maxPoints: number;
    screens: Array<{
      title: string;
      question: {
        text: string;
        points: number;
        options: Array<{
          text: string;
          correct: boolean;
        }>;
        hint?: string;
      };
    }>;
  };
  index: number;
  control: Control<any>;
  onEdit?: () => void;
  onDelete?: () => void;
}

const QuizModule: React.FC<QuizModuleProps> = ({
  quizModule,
  index,
  control,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();

  return (
    <OverviewSection className="!tw-border-gray-border tw-pb-4">
      <div className="tw-flex tw-flex-row tw-mb-6 tw-relative">
        <div className="tw-flex tw-flex-col tw-gap-2 tw-w-full">
          <div className="tw-text-sm tw-text-gray-muted tw-font-inter tw-font-semibold">
            {t('learning:quiz_module_label')}
          </div>
          <div className="tw-flex tw-flex-row tw-gap-1 tw-justify-between">
            <div className="tw-text-xl tw-font-semibold tw-text-gray-900">
              {quizModule.title}
            </div>
            <Dropdown
              className="MembershipRowDropdown tw-ms-auto"
              alignRight
              toggle={<Icon type="more_horiz" size={20} />}
            >
              <Dropdown.Item icon="edit__filled" onClick={onEdit}>
                {t('learning:quiz_module_edit')}
              </Dropdown.Item>
              <ConfirmButton
                title={t('learning:quiz_module_confirm_delete_title')}
                description={t(
                    'learning:quiz_module_confirm_delete_description'
                  )}
                onConfirm={(confirmed) => {
                    if (confirmed && onDelete) {
                      onDelete();
                    }
                  }}
                confirmText={t('learning:quiz_module_confirm_delete_button')}
                danger
              >
                <Dropdown.Item icon="delete__filled">
                  {t('learning:quiz_module_delete')}
                </Dropdown.Item>
              </ConfirmButton>
            </Dropdown>
          </div>
          <div className="tw-flex tw-flex-row tw-items-center tw-gap-3 tw-mt-1 tw-text-sm tw-text-gray-500">
            <div className="tw-flex tw-items-center tw-space-x-1">
              <div
                className="tw-w-4 tw-h-4 tw-rounded tw-bg-no-repeat"
                style={{
                    backgroundImage: `url(${'/static/images/timer-default.png'})`,
                  }}
              />
              <span className="tw-text-base tw-text-gray-muted tw-font-inter tw-font-medium">
                {quizModule.duration}
              </span>
            </div>
            <div className="tw-w-[1px] tw-h-4 tw-flex tw-flex-col tw-bg-gray-border" />
            <div className="tw-flex tw-items-center tw-gap-1">
              <div
                className="tw-w-4 tw-h-4 tw-rounded tw-bg-no-repeat"
                style={{
                    backgroundImage: `url(${'/static/images/layer-three.png'})`,
                  }}
              />
              <span className="tw-text-base tw-text-gray-muted tw-font-inter tw-font-medium">
                {quizModule.screens.length}{' '}
                {t('learning:quiz_module_questions')}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="tw-flex tw-flex-col tw-gap-2">
        {quizModule.screens.map((screen, screenIndex) => {
            return (
              <div key={screen.title} className="tw-gap-3 tw-rounded-[8px] tw-bg-gray-50 QuizModule">
                <Collapsible title={screen.title} index={screenIndex}>
                  <Collapsible.Column flex={1}>
                    <Row>
                      <Group>
                        <FormLabelHookForm
                          name={`questionText_${index}_${screenIndex}`}
                          control={control}
                          minLength={10}
                          maxLength={250}
                          text={t('learning:form_question_question_label')}
                          required
                        />
                        <TextInputHookForm
                          name={`questionText_${index}_${screenIndex}`}
                          control={control}
                          maxLength={250}
                          placeholder={t(
                            'learning:form_question_question_placeholder'
                          )}
                          rules={{
                            required: 'This field is required',
                            minLength: {
                              value: 10,
                              message: 'Minimum 10 characters required',
                            },
                          }}
                        />
                      </Group>
                    </Row>

                    <DndProvider manager={dndManager}>
                      <Row>
                        <OptionsInputHookForm
                          name={`options_${index}_${screenIndex}`}
                          control={control}
                          minAmount={2}
                          screen={screen}
                        />
                      </Row>
                    </DndProvider>
                  </Collapsible.Column>
                </Collapsible>
              </div>
            );
          })}
      </div>
    </OverviewSection>
  );
};

export default QuizModule;
